'use client';

import React from 'react';
import Link from 'next/link';

interface ProjectContactCTAProps {
  heading?: string;
  subHeading?: string;
  description?: string;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  whatsappLink?: string;
  image?: string;
}

const fontWeights = {
  inter: { regular: 400, bold: 700 },
  lato: { bold: 700 },
  asap: { regular: 400 },
};

const ProjectContactCTA = ({
  heading,
  subHeading,
  description,
  primaryButtonText,
  secondaryButtonText,
  whatsappLink,
  image
}: ProjectContactCTAProps) => {
  return (
    <div className="max-w-full mx-auto relative overflow-hidden group">
      {/* Dynamic Background Image Layer */}
      {image && (
        <div className="absolute inset-0 z-0">
          <img 
            src={image} 
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" 
            alt="Background" 
          />
          <div className="absolute inset-0 bg-[#012A49]/90 backdrop-blur-[2px]" />
        </div>
      )}

      {/* Content Layer - removed solid bg if image exists */}
      <div className={`${!image ? 'bg-[#012A49]' : ''} flex flex-col 2xl:flex-row items-center justify-between text-center 2xl:text-left transition-all hover:shadow-2xl border border-white/5 relative z-10
          py-4 
          md:py-6 
          lg:py-8 
          xl:py-12 
          px-10 
          md:px-12 
          lg:px-16 
          xl:px-28 
          gap-5 
          lg:gap-6 
          xl:gap-10`}>

        {/* Text Content */}
        <div className="flex-1 space-y-3 lg:space-y-4 xl:space-y-6">
          <div>
            <h3
              className="text-[22px] sm:text-[28px] md:text-[30px] lg:text-[32px] xl:text-[36px] leading-tight text-white opacity-90"
              style={{ fontFamily: "Lato", fontWeight: fontWeights.lato.bold }}
            >
              {heading || 'Have a question?'}
            </h3>
            <h2
              className="text-[22px] sm:text-[28px] md:text-[30px] lg:text-[32px] xl:text-[36px] leading-tight text-white mb-4"
              style={{ fontFamily: "Lato", fontWeight: fontWeights.lato.bold }}
            >
              {subHeading || 'Our team is happy to assist you'}
            </h2>
          </div>
          <p
            className="text-[15px] md:text-[16px] lg:text-[18px] text-white/80 max-w-[650px] leading-[18px] sm:leading-relaxed mx-auto lg:mx-0"
            style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.regular }}
          >
            {description || "Ask about Bitsshadow impressive portfolio, pricing, development approach, or anything else. Our Expert Consultants are ready to help!"}
          </p>
        </div>

        {/* Buttons */}
        <div className="flex flex-col tsm-350:flex-row items-center gap-2 sm:gap-4 lg:gap-6">
          <button
            onClick={() => window.dispatchEvent(new CustomEvent("openContactPopup"))}
            className="w-full sm:w-auto whitespace-nowrap hover:bg-white/90 inline-flex items-center justify-center bg-white text-[#012A49] rounded-[6px] transition-all shadow-lg
          px-4 
          lg:px-6 
          xl:px-8 
          py-2 
          sm:py-2.5 
          xl:py-4 
          text-[13px] 
          sm:text-[14px] 
          md:text-[16px] 
          lg:text-[18px]"
            style={{ fontFamily: "Inter", fontWeight: fontWeights.inter.bold }}
          >
            {primaryButtonText || 'Request a Quote'}
          </button>
          <Link
            href={whatsappLink || 'https://wa.me/+919925599253?text=Let%27s%20Talk%2C%20I%20have%20a%20query.'}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full sm:w-auto whitespace-nowrap transition-all inline-flex items-center justify-center border-[1px] md:border-[2px] border-white text-white hover:bg-white/10 rounded-[6px] 
          px-4 
          lg:px-6 
          xl:px-8 
          py-2 
          sm:py-2.5 
          xl:py-4 
          text-[13px] 
          sm:text-[14px] 
          md:text-[16px] 
          lg:text-[18px]"
            style={{ fontFamily: "Inter", fontWeight: fontWeights.inter.bold }}
          >
            {secondaryButtonText || 'Schedule a Meeting'}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ProjectContactCTA;
